/**
 * 子 Agent 部分失败之后，父 Agent 该给哪几条出路，以及哪一条不许自动走。
 *
 * 设计稿 S04 的四句话是硬合同：
 *   - 不是它的错（区分「执行单元坏了」与「任务本身有问题」）
 *   - 失败的那个不重开（再开一个还是同一台构建机）
 *   - 完成的那几个结果保留，不重跑（**部分失败不连坐**，这是并行的意义）
 *   - 三条出路里花钱那条走 waiting_user，Lulu 说倾向但不代决
 */
import type { SubAgentRun } from './roster'

export type SubFailExitKind = 'retry' | 'swap_model' | 'takeover'

export interface SubFailExit {
  readonly kind: SubFailExitKind
  readonly copyId:
    | 'lulu_workbench.subfail.exit_retry'
    | 'lulu_workbench.subfail.exit_swap_model'
    | 'lulu_workbench.subfail.exit_takeover'
  /**
   * 这条出路会不会花钱。**花钱的那条永远 `true`，且永远不能自动执行。**
   *
   * 写成数据而不是在组件里 `if (kind === 'swap_model')`：那种写法
   * 在多一条出路时会悄悄漏掉，而漏掉的表现是「平台自己花了钱」。
   */
  readonly costsMoney: boolean
  /** Lulu 的倾向。**至多一条为真**，且它不改变谁需要点击。 */
  readonly preferred: boolean
}

/** 三条出路，顺序固定：重试 → 换模型 → 接管。设计稿就是这个次序。 */
export const SUB_FAIL_EXITS: readonly SubFailExit[] = Object.freeze([
  { kind: 'retry', copyId: 'lulu_workbench.subfail.exit_retry', costsMoney: false, preferred: false },
  {
    kind: 'swap_model',
    copyId: 'lulu_workbench.subfail.exit_swap_model',
    // 换模型 = 换一家供应商跑一遍 = 产生新的调用费用。
    costsMoney: true,
    preferred: false,
  },
  {
    kind: 'takeover',
    copyId: 'lulu_workbench.subfail.exit_takeover',
    costsMoney: false,
    preferred: true,
  },
])

export interface SubFailSummary {
  readonly done: number
  readonly failed: number
  /** 完成的那几个的 id。**它们不进重跑名单**——这是「不连坐」的落点。 */
  readonly keptIds: readonly string[]
  /** 失败的那个也不重开。留着 id 是为了让证据还能查到它。 */
  readonly failedIds: readonly string[]
}

/**
 * 数一轮子 Agent 的结果。
 *
 * **只数终态**：还在跑的既不算完成也不算失败，否则「2 个做完了」这句话
 * 会在中途就冒出来，而那时它是假的。
 */
export function summarizeSubFailure(runs: readonly SubAgentRun[]): SubFailSummary {
  const done = runs.filter((r) => r.state === 'done')
  const failed = runs.filter((r) => r.state === 'fail')
  return {
    done: done.length,
    failed: failed.length,
    keptIds: done.map((r) => r.id),
    failedIds: failed.map((r) => r.id),
  }
}

/**
 * 这一轮要不要停下来问用户。
 *
 * 判据只有一条：**有失败**。不是「失败得够多」，也不是「Lulu 拿不准」——
 * 一个会自己判断「这次不用问」的分支，就是一条能被造出来的旁路。
 */
export function needsUserDecision(summary: SubFailSummary): boolean {
  return summary.failed > 0
}

/**
 * 这条出路可不可以自动执行。
 *
 * **花钱的一律不行**，与 CLAUDE.md 的红级规则同源。这里刻意不看
 * 「用户之前授权过」——设计稿那句「这五类我永远不会替你决定，
 * 哪怕你之前授权过一次」说的就是它。
 */
export function mayAutoRun(exit: SubFailExit): boolean {
  return !exit.costsMoney
}

/**
 * 证据反查：一条证据挂在哪个父工作项上。
 *
 * 子 Agent 的产出**不单独成一条历史**，它挂在父 workItem 下面，
 * 所以从 workItem 能列出这一轮所有子 Agent 的产出。
 */
export interface Evidence {
  readonly id: string
  readonly workItemId: string
  readonly subAgentId: string | null
  readonly label: string
}

export function evidenceForWorkItem(
  all: readonly Evidence[],
  workItemId: string,
): readonly Evidence[] {
  return all.filter((e) => e.workItemId === workItemId)
}

/**
 * 反过来：给一个子 Agent，找到它的产出挂在哪个工作项下。
 *
 * 回 `null` 表示这个子 Agent 还没有产出——**不是「查不到」**。
 * 两者在界面上说的话不同，所以调用方拿到 null 时要自己决定说哪一句。
 */
export function parentItemOfSubAgent(
  all: readonly Evidence[],
  subAgentId: string,
): string | null {
  const hit = all.find((e) => e.subAgentId === subAgentId)
  return hit ? hit.workItemId : null
}
